/**
 * Command Palette Store
 * Manages command palette state with localStorage persistence for recent searches
 *
 * Features:
 * - Open/close state
 * - Search query tracking
 * - Recent searches with localStorage persistence
 * - Selected result for action display
 * - Redux DevTools integration
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { RecentSearch, SearchResult } from '../types';

const MAX_RECENT_SEARCHES = 10;
const STORAGE_KEY = 'byegb-command-palette';

/**
 * Sanitize a RecentSearch entry to ensure subtitle is a string or undefined
 * This handles corrupted data from localStorage where React elements were incorrectly serialized
 */
function sanitizeRecentSearch(search: RecentSearch): RecentSearch {
  return {
    ...search,
    // Only keep subtitle if it's actually a string
    subtitle: typeof search.subtitle === 'string' ? search.subtitle : undefined,
  };
}

/**
 * Sanitize all recent searches loaded from localStorage
 */
function sanitizeRecentSearches(searches: RecentSearch[]): RecentSearch[] {
  if (!Array.isArray(searches)) return [];
  return searches.map(sanitizeRecentSearch).filter((s) => s.resultId && s.title && s.path);
}

interface CommandPaletteState {
  // State
  isOpen: boolean;
  query: string;
  isLoading: boolean;
  selectedResult: SearchResult | null;
  recentSearches: RecentSearch[];

  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  setQuery: (query: string) => void;
  setLoading: (isLoading: boolean) => void;
  setSelectedResult: (result: SearchResult | null) => void;
  addRecentSearch: (search: Omit<RecentSearch, 'timestamp'>) => void;
  removeRecentSearch: (resultId: string) => void;
  clearRecentSearches: () => void;
  reset: () => void;
}

const initialState = {
  isOpen: false,
  query: '',
  isLoading: false,
  selectedResult: null as SearchResult | null,
  recentSearches: [] as RecentSearch[],
};

export const useCommandPaletteStore = create<CommandPaletteState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        open: () => set({ isOpen: true, query: '', selectedResult: null }, false, 'palette/open'),

        close: () =>
          set({ isOpen: false, query: '', selectedResult: null }, false, 'palette/close'),

        toggle: () => {
          const { isOpen } = get();
          if (isOpen) {
            set({ isOpen: false, query: '', selectedResult: null }, false, 'palette/toggle-close');
          } else {
            set({ isOpen: true, query: '', selectedResult: null }, false, 'palette/toggle-open');
          }
        },

        setQuery: (query) => set({ query }, false, 'palette/setQuery'),

        setLoading: (isLoading) => set({ isLoading }, false, 'palette/setLoading'),

        setSelectedResult: (result) =>
          set({ selectedResult: result }, false, 'palette/setSelectedResult'),

        addRecentSearch: (search) =>
          set(
            (state) => {
              // Remove existing entry with same ID if exists
              const filtered = state.recentSearches.filter((s) => s.resultId !== search.resultId);

              // Add new entry at the beginning
              const newEntry: RecentSearch = {
                ...search,
                timestamp: Date.now(),
              };

              // Keep only the most recent entries
              const updated = [newEntry, ...filtered].slice(0, MAX_RECENT_SEARCHES);

              return { recentSearches: updated };
            },
            false,
            'palette/addRecentSearch'
          ),

        removeRecentSearch: (resultId) =>
          set(
            (state) => ({
              recentSearches: state.recentSearches.filter((s) => s.resultId !== resultId),
            }),
            false,
            'palette/removeRecentSearch'
          ),

        clearRecentSearches: () =>
          set({ recentSearches: [] }, false, 'palette/clearRecentSearches'),

        reset: () =>
          set({ ...initialState, recentSearches: get().recentSearches }, false, 'palette/reset'),
      }),
      {
        name: STORAGE_KEY,
        // Only persist recentSearches to localStorage
        partialize: (state) => ({ recentSearches: state.recentSearches }),
        // Sanitize data when loading from localStorage to fix corrupted entries
        onRehydrateStorage: () => (state) => {
          if (state) {
            state.recentSearches = sanitizeRecentSearches(state.recentSearches);
          }
        },
      }
    ),
    {
      name: 'CommandPalette',
      enabled: import.meta.env.DEV,
    }
  )
);

/**
 * Convenience hooks for common patterns (memoized to prevent unnecessary re-renders)
 */
export const useCommandPaletteActions = () => {
  return useCommandPaletteStore(
    useShallow((state) => ({
      open: state.open,
      close: state.close,
      toggle: state.toggle,
      setQuery: state.setQuery,
      setSelectedResult: state.setSelectedResult,
      addRecentSearch: state.addRecentSearch,
      removeRecentSearch: state.removeRecentSearch,
      clearRecentSearches: state.clearRecentSearches,
    }))
  );
};

export const useCommandPaletteState = () => {
  return useCommandPaletteStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      query: state.query,
      isLoading: state.isLoading,
      selectedResult: state.selectedResult,
      recentSearches: state.recentSearches,
    }))
  );
};

/**
 * Hook to check if palette is open (lightweight subscription)
 */
export const useIsCommandPaletteOpen = () => {
  return useCommandPaletteStore((state) => state.isOpen);
};
